define((require) => {
    "use strict";

    const {isEmpty}               = require("underscore");
    const FontProvider            = require("shared/data/fonts/FontProvider");
    const Storage                 = require("shared/services/Storage");
    const FontLoader              = require("shared/util/FontLoader");
    const {trim, isBlank}         = require("shared/util/StringUtils");

    /**
     * The order in which the different font providers are sorted/presented.
     *
     * @type {FontProvider[]}
     */
    const PREFERRED_ORDER = [
        FontProvider.CUSTOM,
        FontProvider.TYPEKIT,
        FontProvider.FONTS_DOT_COM,
        FontProvider.GOOGLE,
        FontProvider.STANDARD
    ];

    /**
     * Controller for the <tt>CharacterPropertiesView</tt>. Responsible for
     * fetching and managing font data.
     *
     * @class CharacterPropertiesViewController
     * @author Kim
     */
    return {

        _fontCollection: null,
        /**
         * @private
         *
         * Invoked directly from the select2 control.
         *
         * @param  {Object} options The options object, containing the search term and callback function.
         *
         * @see  http://ivaynberg.github.io/select2/
         */
        _fontSelectorQuery(options) {
            const term = trim(options.term);

            if (isBlank(term)) {
                // Populate the default result set, including the "favorites"
                options.callback(this._populateResult(this._fontCollection.search(), true));
            } else {
                this._preloadFontsFromSearchTerm(term, options.callback);
            }
        },

        /**
         * Transforms the specified search result into an object that fits the requirement of the select2 control.
         *
         * @param  {Map.<FontProvider, Array>} searchResult A map of search results, where search results are
         * mapped to their respective font provider.
         *
         * @param {Boolean} includeFavorites Optional, whether the populated result set should include the favorite
         * fonts.
         *
         * @return {Object} An object the fits the requirement of the select2 control, see below:
         *
         * @see  http://ivaynberg.github.io/select2/
         * <pre>
         *     {
         *        more: false,
         *        results: [
         *            { text: "Western", children: [
         *                { id: "CA", text: "California" },
         *                { id: "AZ", text: "Arizona" }
         *            ] },
         *            { text: "Eastern", children: [
         *                { id: "FL", text: "Florida" }
         *            ] }
         *        ]
         *     }
         * </pre>
         */
        _populateResult(searchResult, includeFavorites) {
            const results = [];
            let favorites;
            let fontDescriptors;

            searchResult.forEach((fontDescriptors, fontProvider) => {
                if (isEmpty(fontDescriptors)) {
                    return;
                }

                results.push({
                    provider: fontProvider,
                    text: fontProvider.name,
                    children: this._createOptions(fontDescriptors)
                });
            });

            // Sort results according to PREFERRED_ORDER
            results.sort((a, b) => {
                const indexA = PREFERRED_ORDER.indexOf(a.provider);
                const indexB = PREFERRED_ORDER.indexOf(b.provider);

                return indexA > indexB ? 1 : -1;
            });

            if (includeFavorites) {
                // Add the favorites - if any...
                favorites = Storage.Local.get(Storage.Keys.EDITOR, "favoriteFonts", []);

                if (favorites.length) {
                    // Create array of font descriptors from the stored favorites
                    fontDescriptors = favorites.reduce((arr, favorite) => {
                        const fontProvider = FontProvider.fromValue(favorite.fontProvider);
                        const fontDescriptor = this._fontCollection.fromFamily(favorite.fontFamily, fontProvider);

                        if (fontDescriptor) {
                            arr.push(fontDescriptor);
                        }

                        return arr;
                    }, []);

                    // Add the favorites aka. recents at the top...
                    results.unshift({
                        text: "Recents",
                        children: this._createOptions(fontDescriptors)
                    });
                }
            }

            return {
                more: false,
                results
            };
        },

        /**
         * @private
         *
         * Uses the specified search term to perform a lookup for any (Custom and Google Fonts) matches in the
         * font collection. Any results will be loaded using the <tt>FontLoader</tt>.
         *
         * @param  {String}   term     The search term.
         * @param  {Function} callback The callback reference passed from the select2 control.
         */
        _preloadFontsFromSearchTerm(term, callback) {
            const searchResult = this._fontCollection.search(term, 10);

            if (!searchResult.size) {
                // Empty result set
                return callback({
                    results: []
                });
            }

            const googleFontDescriptors = searchResult.get(FontProvider.GOOGLE) || [];
            const customFontDescriptors = searchResult.get(FontProvider.CUSTOM) || [];

            const fontDescriptors = googleFontDescriptors.concat(customFontDescriptors);

            // Start loading the matching fonts (including variants)
            FontLoader.fromFontDescriptors(fontDescriptors, true)
                .then(() => callback(this._populateResult(searchResult)));
        },

        /**
         * @private
         *
         * Creates the array of child data objects passed to select2.
         *
         * @param  {FontDescriptor[]} fontDescriptors Array of <tt>FontDescriptor</tt>'s.
         * @return {Object[]}               Array of objects passed to the select2 control
         *
         * @see  #_populateResult
         */
        _createOptions(fontDescriptors) {
            return fontDescriptors.map((fontDescriptor) => {
                const name = fontDescriptor.get("name");
                const family = fontDescriptor.get("family");

                return {
                    id: family,
                    text: name,
                    fontFamily: family,
                    fontDescriptor
                };
            });
        }
    };
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/CharacterPropertiesViewControllerHelpers.js